import { isTouchDevice } from '../core/utils';

interface GlitchConfig {
  threshold: number;
  decay: number;
  minDuration: number;
}

const CONFIG: GlitchConfig = {
  threshold: 120,
  decay: 0.92,
  minDuration: 120,
};

export const initGlitchEffect = (wrapperSelector = '#page-wrapper'): void => {
  const wrapper = document.querySelector<HTMLElement>(wrapperSelector);
  if (!wrapper) return;

  if (isTouchDevice()) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  let intensity = 0;
  let lastScrollY = window.scrollY;
  let lastTime = performance.now();
  let isActive = false;
  let activeTimeout: number | null = null;
  let rafId: number | null = null;
  let ticking = false;

  const activate = (): void => {
    if (isActive) return;
    isActive = true;
    wrapper.classList.add('glitch-active');
  };

  const deactivate = (): void => {
    if (!isActive) return;
    isActive = false;
    wrapper.classList.remove('glitch-active');
  };

  const update = (): void => {
    ticking = false;

    const now = performance.now();
    const currentScrollY = window.scrollY;
    const deltaTime = now - lastTime;

    if (deltaTime > 0) {
      const deltaScroll = Math.abs(currentScrollY - lastScrollY);
      const rawVelocity = (deltaScroll / deltaTime) * 1000;
      const target = rawVelocity > CONFIG.threshold ? Math.min((rawVelocity - CONFIG.threshold) / 400, 1) : 0;
      intensity = Math.max(intensity * CONFIG.decay, target);
    }

    lastScrollY = currentScrollY;
    lastTime = now;

    if (intensity > 0.08) {
      activate();
      if (activeTimeout) window.clearTimeout(activeTimeout);
      activeTimeout = window.setTimeout(() => {
        intensity = 0;
        deactivate();
      }, CONFIG.minDuration);
    } else if (intensity <= 0.02 && isActive) {
      deactivate();
    }

    wrapper.style.setProperty('--glitch-intensity', String(intensity));
  };

  const onScroll = (): void => {
    if (ticking) return;
    ticking = true;
    rafId = requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });

  const onVisibilityChange = (): void => {
    if (document.hidden) {
      deactivate();
      intensity = 0;
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  const onResize = (): void => {
    lastScrollY = window.scrollY;
  };
  window.addEventListener('resize', onResize, { passive: true });

  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const onMotionPreferenceChange = (event: MediaQueryListEvent): void => {
    if (event.matches) {
      deactivate();
      intensity = 0;
    }
  };
  mediaQuery.addEventListener('change', onMotionPreferenceChange);

  const dispose = (): void => {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('resize', onResize);
    mediaQuery.removeEventListener('change', onMotionPreferenceChange);
    if (rafId) cancelAnimationFrame(rafId);
    if (activeTimeout) window.clearTimeout(activeTimeout);
    deactivate();
  };

  (wrapper as HTMLElement & { __glitchDispose?: () => void }).__glitchDispose = dispose;
};
